/**
 * Reusable black newsletter section (converted from Webflow).
 *
 * The six floating CTA images converge toward center as the section scrolls
 * (Webflow SCROLLING_IN_VIEW a-22, scrubbed). The form validates and shows the
 * original success state client-side (no backend, per the conversion scope).
 */
import type { FC, FormEvent } from 'react'

import { useEffect, useRef, useState } from 'react'

import { gsap, ScrollTrigger } from '@/lib/gsap'

const FLOAT_START: Record<string, [number, number]> = {
    _01: [-20, -10],
    _02: [70, -40],
    _03: [100, 29],
    _04: [-100, 81],
    _05: [-45, 6],
    _06: [100, 79]
}

const IMAGES: Array<{ key: string; src: string; srcSet: string; sizes: string }> = [
    {
        key: '_01',
        src: '/images/CTA-Image-01.jpg',
        srcSet: '/images/CTA-Image-01-p-500.jpg 500w, /images/CTA-Image-01-p-800.jpg 800w, /images/CTA-Image-01-p-1080.jpg 1080w, /images/CTA-Image-01.jpg 1300w',
        sizes: '(max-width: 479px) 120px, 260px'
    },
    {
        key: '_02',
        src: '/images/CTA-Image-03.jpg',
        srcSet: '/images/CTA-Image-03-p-500.jpg 500w, /images/CTA-Image-03-p-800.jpg 800w, /images/CTA-Image-03-p-1080.jpg 1080w, /images/CTA-Image-03.jpg 1300w',
        sizes: '(max-width: 479px) 80px, 160px'
    },
    {
        key: '_03',
        src: '/images/CTA-Image-05.jpg',
        srcSet: '/images/CTA-Image-05-p-500.jpg 500w, /images/CTA-Image-05.jpg 600w',
        sizes: '(max-width: 479px) 90px, 190px'
    },
    {
        key: '_05',
        src: '/images/CTA-Image-04.jpg',
        srcSet: '/images/CTA-Image-04-p-500.jpg 500w, /images/CTA-Image-04-p-800.jpg 800w, /images/CTA-Image-04-p-1080.jpg 1080w, /images/CTA-Image-04.jpg 1300w',
        sizes: '180px'
    },
    {
        key: '_04',
        src: '/images/CTA-Image-06.jpg',
        srcSet: '/images/CTA-Image-06-p-500.jpg 500w, /images/CTA-Image-06.jpg 690w',
        sizes: '120px'
    },
    {
        key: '_06',
        src: '/images/CTA-Image-02.jpg',
        srcSet: '/images/CTA-Image-02-p-500.jpg 500w, /images/CTA-Image-02-p-800.jpg 800w, /images/CTA-Image-02-p-1080.jpg 1080w, /images/CTA-Image-02.jpg 1300w',
        sizes: '(max-width: 479px) 160px, 130px'
    }
]

export const Newsletter: FC = () => {
    const pictureRef = useRef<HTMLDivElement>(null)
    const [email, setEmail] = useState('')
    const [submitted, setSubmitted] = useState(false)

    useEffect(() => {
        const picture = pictureRef.current
        if (!picture) return

        const ctx = gsap.context(() => {
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: picture,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: true
                }
            })

            Object.entries(FLOAT_START).forEach(([key, [x, y]]) => {
                tl.fromTo(
                    `.newsletter-bg-image-container.${key}`,
                    { xPercent: x, yPercent: y },
                    { xPercent: 0, yPercent: 0, ease: 'none' },
                    0
                )
            })
        }, picture)

        ScrollTrigger.refresh()

        return () => ctx.revert()
    }, [])

    const onSubmit = (event: FormEvent) => {
        event.preventDefault()
        if (!email.trim()) return
        setSubmitted(true)
    }

    return (
        <div className='section black-section'>
            <div className='container relative'>
                <div className='newsletter-section-container'>
                    <div className='newsletter-section'>
                        <div className='fade-in-move-on-scroll'>
                            <h3 className='cta-heading-text'>Put your hands first at our premium and limited products</h3>
                        </div>
                        <div className='newsletter-email-section'>
                            <div className='form-block w-form'>
                                {submitted ? (
                                    <div className='success-message gold w-form-done' style={{ display: 'block' }}>
                                        <div>Thank you! Your submission has been received!</div>
                                    </div>
                                ) : (
                                    <form name='wf-form-Newsletter-Email' method='get' className='newsletter-form' onSubmit={onSubmit}>
                                        <div className='fade-in-move-on-scroll _100width'>
                                            <label htmlFor='Newsletter-Email' className='newsletter-field-label'>
                                                Subscribe to our newsletter.
                                            </label>
                                        </div>
                                        <div className='fade-in-move-on-scroll _100width'>
                                            <input
                                                className='newsletter-text-field w-input'
                                                maxLength={256}
                                                name='Newsletter-Email'
                                                placeholder='E-mail'
                                                type='email'
                                                id='Newsletter-Email'
                                                required
                                                value={email}
                                                onChange={(event) => setEmail(event.target.value)}
                                            />
                                        </div>
                                        <div className='fade-in-move-on-scroll _100width'>
                                            <input type='submit' className='button subscribe-button w-button' value='SUBSCRIBE' />
                                        </div>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div ref={pictureRef} className='newsletter-picture-container'>
                {IMAGES.map((image) => (
                    <div key={image.key} className={`newsletter-bg-image-container ${image.key}`}>
                        <img src={image.src} loading='lazy' alt='' sizes={image.sizes} srcSet={image.srcSet} className='cta-image' />
                    </div>
                ))}
            </div>
        </div>
    )
}
